import type { LineItem } from "@graphql/generated/types";
import { List, ListItem } from "@ecco/ui/list";
import ProductRow from "@components/commerce/product/row/ProductRow";
import { getPagePathByLocale, shoppingBagPage } from "@lib/constants/pagePaths";
import { styles } from "./ProductRowListStyle";
import { GIFT_WRAPPING_KEY } from "@lib/constants/product";
import { useRouter } from "@i18n/routing";
import { Flex } from "@ecco/ui/flex";
import { Button } from "@ecco/ui/button";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { CommonCMS } from "@lib/constants/contentful";
import { ReturnLabel } from "@components/cms/return-label/ReturnLabel";
import { useFeatureFlag } from "@lib/feature-flags/FeatureFlagsContext";

interface Props {
    items: LineItem[];
    locale: string;
    isOrderConfirmationPage?: boolean;
}

export const productRowList = "product-row-list";
export const productRowListItem = "product-row-list-item";
export const divider = "divider";

const ProductRowList = ({ items, locale, isOrderConfirmationPage = false }: Props) => {
    const { push } = useRouter();
    const { get: getMicrocopy } = useMicrocopy();
    const isReturnLabelEnabled = useFeatureFlag("isReturnLabelEnabled");
    const handleBack = () => {
        push(`${getPagePathByLocale(shoppingBagPage, locale)}`);
    };

    if (!items?.length) return null;

    const editBagText = getMicrocopy(CommonCMS.page, CommonCMS.editBagText);

    return (
        <>
            <List sx={styles.list} data-testid={productRowList}>
                {items
                    .filter((item) => item?.productKey !== GIFT_WRAPPING_KEY)
                    .map((item) => (
                        <ListItem key={item.id} data-testid={productRowListItem}>
                            <ProductRow key={item.id} item={item} locale={locale} />
                        </ListItem>
                    ))}
                {isReturnLabelEnabled && (
                    <ListItem key="return-label" data-testid={divider}>
                        <ReturnLabel />
                    </ListItem>
                )}
            </List>
            {!isOrderConfirmationPage && (
                <Flex pb={[6, 6, 6, 12]}>
                    <Button
                        variant="link"
                        onClick={handleBack}
                        data-testid="back-to-bag-button"
                        fontWeight={"normal"}
                    >
                        {editBagText}
                    </Button>
                </Flex>
            )}
        </>
    );
};

export default ProductRowList;
